import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, fmtMoney, formatApiErrorDetail } from "@/lib/apiClient";
import { devLog } from "@/lib/devLog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Sparkle, FileText, ClipboardText, Warning, CheckCircle, Upload, FloppyDisk, ArrowRight,
} from "@phosphor-icons/react";

/**
 * BOQWizard — 3-step wizard extracted from RABilling.jsx (iter47 refactor).
 *
 *   Step 1 — Upload the client's Purchase Order / Work Order PDF, extract
 *            header details + BOQ line items via the LLM parser.
 *   Step 2 — Upload the client's BOQ Excel; parser auto-detects columns,
 *            preserves sub-heads, auto-removes total/sub-total rows.
 *   Step 3 — Review + optional Project link → open in the BOQEditorSheet.
 *
 * Props:
 *   • onClose()  — dismiss the wizard
 *   • onReady(previewBoq) — invoked with the fully-composed BOQ payload
 *                          (including `_pending_wo_file` + `_pending_excel_file`
 *                          for post-save attachment upload)
 */
export default function BOQWizard({ onClose, onReady }) {
  const [step, setStep] = useState(1);
  const [woFile, setWoFile] = useState(null);
  const [woParsed, setWoParsed] = useState(null);
  const [woItems, setWoItems] = useState([]);
  const [woSplits, setWoSplits] = useState(null);
  const [woBusy, setWoBusy] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [excelItems, setExcelItems] = useState([]);
  const [excelSkipped, setExcelSkipped] = useState([]);
  const [excelDiag, setExcelDiag] = useState(null);
  const [excelBusy, setExcelBusy] = useState(false);
  const [aiReview, setAiReview] = useState(null);           // AI audit report
  const [aiBusy, setAiBusy] = useState(false);              // Background AI audit call in-flight
  const [aiEnabled, setAiEnabled] = useState(true);         // toggle — default ON
  const [aiRawContext, setAiRawContext] = useState(null);   // { raw_excerpt, sheet_name, header_row, skipped_rows }
  const [towerGroups, setTowerGroups] = useState([]);       // iter55 · multi-tower auto-detect
  const [summaryVerify, setSummaryVerify] = useState(null); // iter55 · summary auto-verify
  const [splitByTower, setSplitByTower] = useState(false);  // toggle — split into N BOQs by tower
  const [originalFileId, setOriginalFileId] = useState("");  // iter56 · client-format fidelity
  const [originalFileName, setOriginalFileName] = useState("");
  const [structureRows, setStructureRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");

  useEffect(() => { (async () => { try { const { data } = await api.get("/projects"); setProjects(data || []); } catch (e) { devLog.warn(e); } })(); }, []);

  const parseWo = async () => {
    if (!woFile) return toast.error("Pick the Client PO/WO PDF first");
    setWoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", woFile);
      const { data } = await api.post("/projects/parse-wo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setWoParsed(data.parsed || {});
      setWoItems(data.items || []);
      setWoSplits(data.payment_splits || null);
      toast.success(`Extracted from PO — ${(data.items || []).length} BOQ item(s) also found. Continue to Step 2 to upload BOQ Excel (or skip if items are enough).`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setWoBusy(false); }
  };

  const parseExcel = async () => {
    if (!excelFile) return toast.error("Pick the BOQ Excel first");
    setExcelBusy(true); setExcelDiag(null); setAiReview(null); setAiRawContext(null);
    setExcelItems([]); setExcelSkipped([]); setTowerGroups([]); setSummaryVerify(null);
    setOriginalFileId(""); setOriginalFileName(""); setStructureRows([]);
    try {
      const fd = new FormData();
      fd.append("file", excelFile);
      // Do NOT set use_ai_review — we call /boqs/ai-review as a second-phase
      // call so the initial parse response is fast (< 3 s) and the AI audit
      // runs asynchronously with a progress indicator the user can watch.
      const { data } = await api.post("/boqs/upload-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!(data.items || []).length) {
        setExcelDiag({ message: "Parser found 0 items — check your header row.", ...data });
        toast.warning(`0 items found in sheet "${data.sheet_used}" — see diagnostics below`);
        return;
      }
      setExcelItems(data.items || []);
      setExcelSkipped(data.skipped_total_rows || []);
      setTowerGroups(data.tower_groups || []);
      setSummaryVerify(data.summary_verification || null);
      setOriginalFileId(data.original_file_id || "");
      setOriginalFileName(data.original_file_name || "");
      setStructureRows(data.structure_rows || []);
      // Default the toggle ON when the workbook clearly has multiple towers
      if ((data.tower_groups || []).length >= 2) setSplitByTower(true);
      setAiRawContext({
        raw_excerpt: data.raw_excerpt || "",
        sheet_name: data.sheet_used || "",
        header_row: data.header_row || 0,
        skipped_rows: data.skipped_total_rows || [],
      });
      const skippedN = (data.skipped_total_rows || []).length;
      const tCount = (data.tower_groups || []).length;
      const sv = data.summary_verification;
      const svExtra = sv && sv.all_match ? " · Summary verified ✓" : (sv && sv.total_checks > 0 ? ` · Summary check ${sv.match_count}/${sv.total_checks}` : "");
      const tExtra = tCount >= 2 ? ` · ${tCount} towers detected` : "";
      toast.success(`Parsed ${data.items.length} item(s) from "${data.sheet_used}"${skippedN ? ` · ${skippedN} total/sub-total row(s) auto-removed` : ""}${tExtra}${svExtra}${aiEnabled ? " · Running AI audit…" : ""}`);

      // ---- Phase 2: background AI review ----
      if (aiEnabled) {
        setAiBusy(true);
        try {
          const { data: aiData } = await api.post("/boqs/ai-review", {
            items: data.items || [],
            skipped_rows: data.skipped_total_rows || [],
            raw_excerpt: data.raw_excerpt || "",
            sheet_name: data.sheet_used || "",
            header_row: data.header_row || 0,
          }, { timeout: 60000 });
          setAiReview(aiData.ai_review || null);
          if (aiData.items) setExcelItems(aiData.items);   // apply any corrections
          const ai = aiData.ai_review;
          if (ai && ai.status === "clean") toast.success("AI audit passed — no issues found ✓");
          else if (ai && ai.status === "skipped") toast.info(`AI audit skipped: ${ai.reason || "unknown"}`);
          else if (ai && (ai.issues || []).length) toast.warning(`AI audit found ${(ai.issues || []).length} issue(s) — see panel below`);
        } catch (e) {
          devLog.warn("AI review call failed:", e);
          setAiReview({ status: "skipped", reason: `network_error: ${e.message}` });
          toast.info("AI audit unavailable — deterministic parse only");
        } finally {
          setAiBusy(false);
        }
      }
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setExcelDiag({ message: msg });
      toast.error(msg);
    }
    finally { setExcelBusy(false); }
  };

  const rerunAiReview = async () => {
    if (!aiRawContext || !excelItems.length) return;
    setAiBusy(true); setAiReview(null);
    try {
      const { data: aiData } = await api.post("/boqs/ai-review", {
        items: excelItems,
        skipped_rows: aiRawContext.skipped_rows || [],
        raw_excerpt: aiRawContext.raw_excerpt || "",
        sheet_name: aiRawContext.sheet_name || "",
        header_row: aiRawContext.header_row || 0,
      }, { timeout: 60000 });
      setAiReview(aiData.ai_review || null);
      if (aiData.items) setExcelItems(aiData.items);
      toast.success("AI audit re-run complete");
    } catch (e) {
      devLog.warn("AI re-review failed:", e);
      setAiReview({ status: "skipped", reason: `network_error: ${e.message}` });
      toast.error("AI audit failed — please try again");
    } finally {
      setAiBusy(false);
    }
  };

  const finalItems = useMemo(() => (excelItems.length ? excelItems : woItems), [excelItems, woItems]);
  const totalValue = useMemo(() => finalItems.reduce((s, it) => s + (Number(it.contract_qty) || 0) * (Number(it.rate) || 0), 0), [finalItems]);

  const proceedToEditor = () => {
    if (!finalItems.length && !woParsed) return toast.error("Upload at least the WO or the BOQ Excel first");
    const p = woParsed || {};

    // iter55: If user picked "Split into separate BOQs per tower", emit ONE
    // preview PER tower so the caller opens N BOQ editors in sequence.
    // Otherwise fall through to the single combined-preview path.
    if (splitByTower && towerGroups.length >= 2) {
      const previews = towerGroups.map((g) => {
        const towerItems = finalItems.filter((it) => (it.tower || "") === g.tower);
        return {
          project_id: (projectId && projectId !== "__none") ? projectId : undefined,
          boq_title: `${p.name_of_work || excelFile?.name?.replace(/\.xlsx?$/i, "") || "Client BOQ"} — ${g.tower}`,
          client_name: p.client_name || "",
          client_address: p.client_address || "",
          client_gstin: p.client_gstin || "",
          client_state: p.client_state || "",
          work_order_no: p.work_order_no || "",
          work_order_date: p.work_order_date || "",
          work_order_value: g.total || 0,
          agreement_no: p.agreement_no || "",
          name_of_work: `${p.name_of_work || ""} · ${g.tower}`.trim(),
          sub_project: g.tower,
          sub_head: p.sub_head || "MEP",
          site: p.site || "",
          contractor_name: p.contractor_name || "Celsius",
          contractor_pan: p.contractor_pan || "",
          contractor_gstin: p.contractor_gstin || "",
          contractor_state: p.contractor_state || "",
          is_interstate: typeof p.is_interstate === "boolean" ? p.is_interstate : false,
          default_supply_pct: woSplits?.supply_pct || 85,
          default_install_pct: woSplits?.install_pct || 10,
          default_tnc_pct: woSplits?.tnc_pct || 5,
          retention_pct: woSplits?.retention_pct || 5,
          tds_pct: woSplits?.tds_pct || 1,
          default_gst_rate: 18,
          items: towerItems.map((it, i) => ({
            sr_no: it.sr_no || String(i + 1),
            section: it.section || "",
            description: it.description,
            unit: it.unit || "Nos",
            hsn: it.hsn || "",
            contract_qty: Number(it.contract_qty) || 0,
            rate: Number(it.rate) || 0,
            amount: Number(it.amount) || Number(it.contract_qty) * Number(it.rate) || 0,
            supply_pct: it.supply_pct ?? (woSplits?.supply_pct || 85),
            install_pct: it.install_pct ?? (woSplits?.install_pct || 10),
            tnc_pct: it.tnc_pct ?? (woSplits?.tnc_pct || 5),
            gst_rate: it.gst_rate ?? 18,
            notes: it.notes || "",
            specs: it.specs || undefined,
            tower: g.tower,
          })),
          // iter56 · original file shared across tower splits so both BOQs
          // can regenerate exports in the client's exact format.
          original_file_id: originalFileId,
          original_file_name: originalFileName,
          structure_rows: structureRows.filter((sr) => (sr.tower || "") === g.tower || sr.tower === ""),
        };
      });
      toast.success(`Split into ${previews.length} tower BOQs — opening editors one by one.`);
      onReady?.(previews);
      return;
    }

    const preview = {
      project_id: (projectId && projectId !== "__none") ? projectId : undefined,
      boq_title: p.name_of_work || (excelFile?.name?.replace(/\.xlsx?$/i, "") || "Client BOQ"),
      client_name: p.client_name || "",
      client_address: p.client_address || "",
      client_gstin: p.client_gstin || "",
      client_state: p.client_state || "",
      work_order_no: p.work_order_no || "",
      work_order_date: p.work_order_date || "",
      work_order_value: p.work_order_value || 0,
      agreement_no: p.agreement_no || "",
      name_of_work: p.name_of_work || "",
      sub_project: p.sub_project || "",
      sub_head: p.sub_head || "MEP",
      site: p.site || "",
      contractor_name: p.contractor_name || "Celsius",
      contractor_pan: p.contractor_pan || "",
      contractor_gstin: p.contractor_gstin || "",
      contractor_state: p.contractor_state || "",
      is_interstate: typeof p.is_interstate === "boolean" ? p.is_interstate : false,
      default_supply_pct: woSplits?.supply_pct || 85,
      default_install_pct: woSplits?.install_pct || 10,
      default_tnc_pct: woSplits?.tnc_pct || 5,
      retention_pct: woSplits?.retention_pct || 5,
      tds_pct: woSplits?.tds_pct || 1,
      default_gst_rate: 18,
      items: finalItems.map((it, i) => ({
        sr_no: it.sr_no || String(i + 1),
        section: it.section || (excelItems.length ? "" : "From Client PO/WO"),
        description: it.description,
        unit: it.unit || "Nos",
        hsn: it.hsn || "",
        contract_qty: Number(it.contract_qty) || 0,
        rate: Number(it.rate) || 0,
        amount: Number(it.amount) || Number(it.contract_qty) * Number(it.rate) || 0,
        supply_pct: it.supply_pct ?? (woSplits?.supply_pct || 85),
        install_pct: it.install_pct ?? (woSplits?.install_pct || 10),
        tnc_pct: it.tnc_pct ?? (woSplits?.tnc_pct || 5),
        gst_rate: it.gst_rate ?? 18,
        notes: it.notes || "",
        specs: it.specs || undefined,
        tower: it.tower || "",
      })),
      _pending_wo_file: woFile,
      _pending_excel_file: excelFile,
      // iter56: carry the original client file + structure so
      // downloads / client-format exports work on this BOQ after save.
      original_file_id: originalFileId,
      original_file_name: originalFileName,
      structure_rows: structureRows,
    };
    onReady?.(preview);
  };

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[min(1000px,100vw)] sm:max-w-none overflow-y-auto" data-testid="boq-wizard-sheet">
        <SheetHeader className="mb-4">
          <SheetTitle className="font-display flex items-center gap-2">
            <Sparkle size={18} className="text-[#EA580C]" /> New Client BOQ · Step {step} of 3
          </SheetTitle>
          <SheetDescription className="text-[11.5px] text-slate-500">
            Upload the client's Purchase Order / Work Order (PDF) to auto-fetch header details, then upload the BOQ Excel — save as one Client BOQ.
          </SheetDescription>
        </SheetHeader>

        <div className="flex gap-2 mb-6">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`flex-1 h-1.5 rounded-sm ${step >= n ? "bg-[#EA580C]" : "bg-slate-200"}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4" data-testid="wizard-step-1">
            <div className="border border-[#0F172A]/15 bg-gradient-to-br from-slate-50 to-white rounded-sm p-4">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-[#EA580C] mb-1 flex items-center gap-1.5"><FileText size={13} /> Step 1 — Client Purchase Order / Work Order</div>
              <div className="text-[11.5px] text-slate-600 mb-3">Upload the client's signed PO/WO (PDF or image). AI will extract WO#, date, value, agreement, both parties' GSTINs and payment terms into the header — no re-typing.</div>
              <Input type="file" accept="application/pdf,image/*" onChange={(e) => setWoFile(e.target.files?.[0] || null)} data-testid="wizard-wo-file" />
              <div className="mt-3 flex gap-2">
                <Button onClick={parseWo} disabled={!woFile || woBusy} className="rounded-sm bg-gradient-to-r from-[#EA580C] to-[#c2410c] text-white font-semibold" data-testid="wizard-parse-wo">
                  <Sparkle size={13} className="mr-1.5" /> {woBusy ? "Extracting…" : "Extract PO details"}
                </Button>
                <Button variant="outline" onClick={() => setStep(2)} className="rounded-sm" data-testid="wizard-skip-wo">Skip → BOQ Excel</Button>
              </div>
            </div>

            {woParsed && (
              <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-3">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-emerald-700 flex items-center gap-1.5 mb-2"><CheckCircle size={13} /> Extracted details</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5 text-[11.5px]">
                  <div><span className="text-slate-500">Client:</span> <span className="font-semibold">{woParsed.client_name || "—"}</span></div>
                  <div><span className="text-slate-500">WO#:</span> <span className="font-mono">{woParsed.work_order_no || "—"}</span></div>
                  <div><span className="text-slate-500">Value:</span> <span className="font-mono-num">{fmtMoney(woParsed.work_order_value || 0)}</span></div>
                  <div><span className="text-slate-500">Agreement:</span> <span className="font-mono">{woParsed.agreement_no || "—"}</span></div>
                  <div><span className="text-slate-500">Site:</span> {woParsed.site || "—"}</div>
                  <div><span className="text-slate-500">Sub Head:</span> {woParsed.sub_head || "—"}</div>
                  <div><span className="text-slate-500">Client GSTIN:</span> <span className="font-mono">{woParsed.client_gstin || "—"}</span></div>
                  <div><span className="text-slate-500">Contractor GSTIN:</span> <span className="font-mono">{woParsed.contractor_gstin || "—"}</span></div>
                  <div><span className="text-slate-500">Regime:</span> {woParsed.is_interstate ? "IGST 18%" : "CGST+SGST 18%"}</div>
                </div>
                {woSplits && (
                  <div className="text-[11px] text-emerald-800 mt-2 pt-2 border-t border-emerald-200">Payment splits detected: <b>Supply {woSplits.supply_pct}% · Install {woSplits.install_pct}% · T&C {woSplits.tnc_pct}%</b> · Retention {woSplits.retention_pct}% · TDS {woSplits.tds_pct}%</div>
                )}
                {woItems.length > 0 && (
                  <div className="text-[11px] text-slate-700 mt-2 pt-2 border-t border-emerald-200">
                    <b>{woItems.length} BOQ item(s)</b> also extracted from the WO. You can proceed to Step 3 directly with these, or upload the BOQ Excel in Step 2 to replace them.
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
              <Button onClick={() => setStep(2)} disabled={!woParsed && !woFile} className="rounded-sm bg-[#0F172A] hover:bg-black text-white" data-testid="wizard-step-1-next">
                Next → BOQ Excel <ArrowRight size={13} className="ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="wizard-step-2">
            <div className="border border-[#0F172A]/15 bg-gradient-to-br from-slate-50 to-white rounded-sm p-4">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-[#EA580C] mb-1 flex items-center gap-1.5"><ClipboardText size={13} /> Step 2 — Client BOQ Excel</div>
              <div className="text-[11.5px] text-slate-600 mb-3">Upload the BOQ .xlsx file (the client's Bill of Quantities). Parser auto-detects <b>Description · Qty · Rate</b> columns on any sheet — supports "ABSTRACT", "BOQ", "Schedule" or custom sheet names.</div>
              <Input type="file" accept=".xlsx" onChange={(e) => setExcelFile(e.target.files?.[0] || null)} data-testid="wizard-excel-file" />
              <label className="mt-3 flex items-center gap-2 text-[11.5px] text-slate-700 select-none cursor-pointer" data-testid="wizard-ai-toggle-label">
                <input
                  type="checkbox"
                  className="accent-[#EA580C]"
                  checked={aiEnabled}
                  onChange={(e) => setAiEnabled(e.target.checked)}
                  data-testid="wizard-ai-toggle"
                />
                <Sparkle size={12} className="text-[#EA580C]" />
                <span><b>AI-monitored parse</b> — Gemini reviews every row, auto-detects missed items, wrong section grouping, and non-default payment splits. Recommended.</span>
              </label>
              <div className="mt-3 flex gap-2">
                <Button onClick={parseExcel} disabled={!excelFile || excelBusy} className="rounded-sm bg-gradient-to-r from-[#EA580C] to-[#c2410c] text-white font-semibold" data-testid="wizard-parse-excel">
                  <Upload size={13} className="mr-1.5" /> {excelBusy ? "Parsing…" : "Parse BOQ Excel"}
                </Button>
                {woItems.length > 0 && (
                  <Button variant="outline" onClick={() => setStep(3)} className="rounded-sm" data-testid="wizard-use-wo-items">
                    Use WO items ({woItems.length}) →
                  </Button>
                )}
              </div>
            </div>

            {excelDiag && (
              <div className="border border-amber-200 bg-amber-50 rounded-sm p-3 text-[11.5px] text-slate-700 space-y-1">
                <div className="font-semibold text-amber-800 flex items-center gap-1"><Warning size={12} /> {excelDiag.message}</div>
                {excelDiag.sheets_found && <div><span className="font-mono text-slate-500">sheets:</span> {excelDiag.sheets_found.join(", ")}</div>}
                {excelDiag.sheet_used && <div><span className="font-mono text-slate-500">parsed sheet:</span> {excelDiag.sheet_used}</div>}
                {typeof excelDiag.header_row === "number" && <div><span className="font-mono text-slate-500">header row:</span> {excelDiag.header_row}</div>}
                {excelDiag.detected_columns && <div><span className="font-mono text-slate-500">columns:</span> {JSON.stringify(excelDiag.detected_columns)}</div>}
                <div className="text-slate-500 pt-1">Tip: ensure your sheet has a row with column labels like "Description", "Qty" and "Rate".</div>
              </div>
            )}

            {(aiBusy || aiReview) && (() => {
              if (aiBusy && !aiReview) {
                return (
                  <div className="border border-orange-200 bg-orange-50 rounded-sm p-3 text-[11.5px] text-orange-900 flex items-center gap-2" data-testid="wizard-ai-busy">
                    <Sparkle size={14} className="animate-pulse" />
                    <div className="flex-1">
                      <div className="font-semibold">AI Auditor running…</div>
                      <div className="opacity-80 text-[11px]">Gemini is reviewing every parsed row for missed items, wrong section grouping, and non-default payment splits. Usually 15-25 seconds.</div>
                    </div>
                  </div>
                );
              }
              if (!aiReview) return null;
              const isClean = aiReview.status === "clean";
              const isSkipped = aiReview.status === "skipped";
              const issues = aiReview.issues || [];
              const applied = aiReview.applied_corrections || [];
              const cls = isClean
                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                : isSkipped
                ? "border-slate-200 bg-slate-50 text-slate-700"
                : (aiReview.status === "errors"
                    ? "border-red-200 bg-red-50 text-red-900"
                    : "border-amber-200 bg-amber-50 text-amber-900");
              return (
                <div className={`border rounded-sm p-3 text-[11.5px] ${cls}`} data-testid="wizard-ai-review-panel">
                  <div className="flex items-start gap-2 mb-1.5">
                    <Sparkle size={14} className="mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold flex items-center flex-wrap gap-x-2 gap-y-0.5">
                        <span data-testid="wizard-ai-status">
                          {isClean && "AI Verified — No issues found"}
                          {isSkipped && `AI Review Skipped${aiReview.reason ? ` (${aiReview.reason})` : ""}`}
                          {!isClean && !isSkipped && `AI found ${issues.length} issue${issues.length !== 1 ? "s" : ""}`}
                        </span>
                        {typeof aiReview.confidence === "number" && (
                          <span className="text-[10px] font-mono rounded-sm bg-white/60 border border-current/10 px-1.5 py-0.5">
                            confidence {aiReview.confidence}%
                          </span>
                        )}
                        {applied.length > 0 && (
                          <span className="text-[10px] font-mono rounded-sm bg-white/70 border border-current/10 px-1.5 py-0.5" data-testid="wizard-ai-applied">
                            {applied.length} auto-fix{applied.length !== 1 ? "es" : ""} applied
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={rerunAiReview}
                          disabled={aiBusy || !excelItems.length}
                          className="ml-auto text-[10px] font-semibold underline decoration-dotted hover:no-underline disabled:opacity-40"
                          data-testid="wizard-ai-rerun"
                        >
                          Re-run audit
                        </button>
                      </div>
                      {aiReview.summary && <div className="text-[11px] opacity-80 mt-0.5">{aiReview.summary}</div>}
                    </div>
                  </div>
                  {issues.length > 0 && (
                    <details className="mt-1.5" data-testid="wizard-ai-issues">
                      <summary className="cursor-pointer text-[11px] font-semibold underline decoration-dotted">
                        View all {issues.length} issue{issues.length !== 1 ? "s" : ""}
                      </summary>
                      <div className="mt-1.5 pl-1 space-y-1">
                        {issues.slice(0, 20).map((iss, i) => (
                          <div key={`ai-iss-${i}-${iss.type}`} className="text-[10.5px] leading-snug">
                            <span className="font-mono opacity-60">[{iss.type}·{iss.severity || "med"}]</span>{" "}
                            {typeof iss.target_index === "number" && <span className="font-mono opacity-70">item #{iss.target_index + 1}: </span>}
                            {typeof iss.raw_row === "number" && <span className="font-mono opacity-70">row {iss.raw_row}: </span>}
                            {iss.description}
                            {iss.suggestion && <span className="opacity-70"> → <i>{iss.suggestion}</i></span>}
                          </div>
                        ))}
                        {issues.length > 20 && <div className="text-[10px] opacity-60">... + {issues.length - 20} more</div>}
                      </div>
                    </details>
                  )}
                  {applied.length > 0 && (
                    <details className="mt-1 text-[10.5px]" data-testid="wizard-ai-applied-details">
                      <summary className="cursor-pointer underline decoration-dotted">
                        View {applied.length} auto-applied correction{applied.length !== 1 ? "s" : ""}
                      </summary>
                      <div className="mt-1 pl-1 space-y-0.5 font-mono">
                        {applied.map((c, i) => (
                          <div key={`ai-fix-${i}-${c.action}`}>
                            · item #{(c.target_index ?? 0) + 1}: {c.action}
                            {c.new_section && ` → section = "${c.new_section}"`}
                            {c.action === "update_milestone" && ` → S ${c.supply_pct}% / I ${c.install_pct}% / T ${c.tnc_pct}%`}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              );
            })()}

            {/* iter55: Summary Auto-Verification */}
            {summaryVerify && summaryVerify.total_checks > 0 && (
              <div
                className={`border rounded-sm p-3 text-[11.5px] ${
                  summaryVerify.all_match ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"
                }`}
                data-testid="wizard-summary-verify"
              >
                <div className="flex items-center gap-2 mb-1.5">
                  <ClipboardText size={13} className={summaryVerify.all_match ? "text-emerald-700" : "text-amber-700"} />
                  <span className={`font-bold uppercase tracking-widest text-[10px] ${summaryVerify.all_match ? "text-emerald-800" : "text-amber-800"}`}>
                    Summary Sheet Verification
                  </span>
                  <span className={`ml-auto text-[10px] font-mono rounded-sm px-1.5 py-0.5 border ${
                    summaryVerify.all_match ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-amber-100 border-amber-300 text-amber-800"
                  }`} data-testid="wizard-summary-verify-status">
                    {summaryVerify.match_count}/{summaryVerify.total_checks} matched
                    {summaryVerify.all_match ? " ✓" : ""}
                  </span>
                </div>
                <div className="space-y-1">
                  {summaryVerify.checks.map((c, i) => (
                    <div key={`sv-${i}-${c.summary_sheet}`} className="flex items-center gap-2 font-mono text-[10.5px]">
                      <span className={`w-5 text-center ${c.matches ? "text-emerald-600" : "text-red-600"}`}>{c.matches ? "✓" : "✗"}</span>
                      <span className="w-40 truncate text-slate-700">{c.summary_sheet}</span>
                      {c.tower && <span className="text-[9px] bg-slate-100 border border-slate-200 rounded px-1 py-0 text-slate-600">{c.tower}</span>}
                      <span className="text-slate-500">declared</span>
                      <span className="w-32 text-right">₹{Number(c.declared_total).toLocaleString("en-IN")}</span>
                      <span className="text-slate-500">extracted</span>
                      <span className="w-32 text-right">₹{Number(c.extracted_total).toLocaleString("en-IN")}</span>
                      {c.diff > 0 && <span className="text-amber-700 text-[10px]">diff ₹{Number(c.diff).toLocaleString("en-IN")}</span>}
                    </div>
                  ))}
                </div>
                <div className="mt-2 pt-1.5 border-t border-current/10 flex items-center gap-2 text-[10.5px]">
                  <span className="text-slate-600 font-semibold">GRAND TOTAL</span>
                  <span className="ml-auto font-mono">Declared <b>₹{Number(summaryVerify.grand_declared).toLocaleString("en-IN")}</b> · Extracted <b>₹{Number(summaryVerify.grand_extracted).toLocaleString("en-IN")}</b></span>
                </div>
              </div>
            )}

            {/* iter55: Multi-Tower Auto-Split */}
            {towerGroups.length >= 2 && (
              <div className="border border-indigo-200 bg-indigo-50 rounded-sm p-3 text-[11.5px]" data-testid="wizard-tower-groups">
                <div className="flex items-center gap-2 mb-2">
                  <ClipboardText size={13} className="text-indigo-700" />
                  <span className="font-bold uppercase tracking-widest text-[10px] text-indigo-800">Multi-Tower Detected · {towerGroups.length} Towers</span>
                  <label className="ml-auto flex items-center gap-1.5 cursor-pointer text-[11px] select-none" data-testid="wizard-split-tower-label">
                    <input
                      type="checkbox"
                      className="accent-[#EA580C]"
                      checked={splitByTower}
                      onChange={(e) => setSplitByTower(e.target.checked)}
                      data-testid="wizard-split-tower-toggle"
                    />
                    <span>Save as <b>{splitByTower ? "separate BOQs per tower" : "one combined BOQ"}</b></span>
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {towerGroups.map((g) => (
                    <div key={g.tower} className="bg-white border border-indigo-100 rounded-sm p-2" data-testid={`wizard-tower-${g.tower}`}>
                      <div className="font-semibold text-indigo-800">{g.tower}</div>
                      <div className="text-slate-600 text-[10.5px] font-mono">{g.item_count} items · ₹{Number(g.total).toLocaleString("en-IN")}</div>
                    </div>
                  ))}
                </div>
                {splitByTower && (
                  <div className="mt-2 text-[10.5px] text-indigo-700 pl-4 border-l-2 border-indigo-300">
                    On save, this will create <b>{towerGroups.length} independent BOQs</b> — one per tower — so RA bills stay tower-wise.
                  </div>
                )}
              </div>
            )}

            {excelItems.length > 0 && (() => {
              const bySection = excelItems.reduce((acc, it) => {
                const sec = (it.section || "").trim() || "— ungrouped —";
                (acc[sec] = acc[sec] || []).push(it);
                return acc;
              }, {});
              // iter55: detect which spec keys are present across items
              const specKeys = new Set();
              for (const it of excelItems) {
                if (it.specs) Object.keys(it.specs).forEach((k) => specKeys.add(k));
              }
              const orderedSpecKeys = ["type", "cfm", "sp", "kw", "dia", "tr", "area"].filter((k) => specKeys.has(k));
              return (
                <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-3" data-testid="wizard-parsed-preview">
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-emerald-700 flex items-center gap-1.5 mb-2 flex-wrap">
                    <CheckCircle size={13} /> {excelItems.length} item(s) parsed
                    <span className="text-slate-500 font-normal normal-case">· Client sub-heads preserved as {Object.keys(bySection).length} section(s)</span>
                    {orderedSpecKeys.length > 0 && (
                      <span className="ml-2 text-[10px] font-mono rounded-sm bg-indigo-100 border border-indigo-200 text-indigo-800 px-1.5 py-0.5" data-testid="wizard-spec-columns">
                        specs: {orderedSpecKeys.join(" · ")}
                      </span>
                    )}
                    {excelSkipped.length > 0 && (
                      <span className="ml-2 text-[10px] font-mono rounded-sm bg-amber-100 border border-amber-200 text-amber-800 px-1.5 py-0.5" data-testid="wizard-skipped-totals">
                        {excelSkipped.length} total row(s) auto-removed
                      </span>
                    )}
                  </div>
                  <div className="max-h-52 overflow-y-auto text-[11px] space-y-2">
                    {Object.entries(bySection).map(([sec, its]) => (
                      <div key={sec}>
                        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">{sec} <span className="text-slate-400 normal-case">· {its.length} item(s)</span></div>
                        {its.slice(0, 6).map((it, i) => (
                          <div key={`${sec}-${it.sr_no || it.description || i}`} className="font-mono text-slate-600 pl-3">
                            • #{it.sr_no || i + 1} · {it.description?.slice(0, 55)} · qty={it.contract_qty} · rate=₹{Number(it.rate).toLocaleString("en-IN")}
                            {it.specs && (
                              <span className="ml-2 text-[9.5px] text-indigo-700">
                                {orderedSpecKeys.filter((k) => it.specs[k] != null).map((k) => `${k}:${it.specs[k]}`).join(" · ")}
                              </span>
                            )}
                          </div>
                        ))}
                        {its.length > 6 && <div className="text-slate-400 pl-3">... + {its.length - 6} more</div>}
                      </div>
                    ))}
                  </div>
                  {excelSkipped.length > 0 && (
                    <details className="mt-2 text-[10.5px] text-slate-600" data-testid="wizard-skipped-details">
                      <summary className="cursor-pointer text-amber-800 hover:text-amber-900">View {excelSkipped.length} auto-removed total/sub-total row(s)</summary>
                      <div className="mt-1 pl-3 space-y-0.5 font-mono">
                        {excelSkipped.map((s, i) => (<div key={`skip-${s.row ?? i}`}>· row {s.row}: {s.description}{s.amount ? ` (₹${Number(s.amount).toLocaleString("en-IN")})` : ""}</div>))}
                      </div>
                    </details>
                  )}
                </div>
              );
            })()}

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-sm">← Back</Button>
              <Button onClick={() => setStep(3)} disabled={!excelItems.length && !woItems.length} className="rounded-sm bg-[#0F172A] hover:bg-black text-white" data-testid="wizard-step-2-next">
                Next → Review <ArrowRight size={13} className="ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="wizard-step-3">
            <div className="border border-[#0F172A]/15 bg-white rounded-sm p-4">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-[#EA580C] mb-2 flex items-center gap-1.5"><FloppyDisk size={13} /> Step 3 — Review & Save</div>
              <div className="text-[11.5px] text-slate-600 mb-3">Optionally link to an existing Project (WO details will merge). You'll be able to edit every detail in the next screen before final save.</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">Link to Project (optional)</Label>
                  <Select value={projectId} onValueChange={setProjectId}>
                    <SelectTrigger data-testid="wizard-project"><SelectValue placeholder="No project — standalone BOQ" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">— none —</SelectItem>
                      {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.client_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1 text-[11.5px]">
                  <div className="flex justify-between"><span className="text-slate-500">Items to import:</span> <span className="font-mono-num font-semibold">{finalItems.length}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Total value:</span> <span className="font-mono-num font-semibold">{fmtMoney(totalValue)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Client:</span> <span className="font-semibold">{woParsed?.client_name || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">WO#:</span> <span className="font-mono">{woParsed?.work_order_no || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Attachments:</span> <span>{[woFile && "PO/WO", excelFile && "BOQ Excel"].filter(Boolean).join(" + ") || "—"}</span></div>
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)} className="rounded-sm">← Back</Button>
              <Button onClick={proceedToEditor} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white font-semibold" data-testid="wizard-save">
                <ArrowRight size={13} className="mr-1" /> Open in Editor
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
